/**
 * The controls at the right of the header: the arrangement toggle, and Save / My Reports /
 * Share.
 */

import { useRef, useState } from 'react';
import {
    Box,
    Button,
    ButtonGroup,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ShareIcon from '@mui/icons-material/Share';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';

import type { SummaryReportLayout } from '../../utils/layout.ts';
import type { SummaryReportDialogMode, SummaryReportStatus } from '../../types.ts';

// ─── Props ───────────────────────────────────────────────────────────────────

interface SummaryReportToolbarProps {
    /** Name of the loaded report, if any. */
    activeReportName?: string;
    /**
     * Where the view stands relative to the loaded report. The save button's label follows
     * it, so the header says what pressing it would do.
     */
    reportStatus?: SummaryReportStatus;
    /** Whether a chart pane is on screen. There is nothing to arrange without one. */
    chartVisible?: boolean;
    /** Opens the dialog in a given mode. */
    onOpenDialog: (mode: SummaryReportDialogMode) => void;
    /** The current arrangement of the grid and chart. */
    layout: SummaryReportLayout;
    /** Called when the user picks a different arrangement. */
    onLayoutChange: (layout: SummaryReportLayout) => void;
}

const raisedControlSx = {
    backgroundColor: 'background.paper',
    boxShadow: 1,
    '&:hover': { backgroundColor: 'background.paper' },
} as const;

const greyedSurface = {
    backgroundColor: 'grey.100',
    color: 'action.disabled',
    borderColor: 'grey.400',
    opacity: 0.8,
} as const;

/** A step up from MUI's 11px tooltip type, which is small against these controls. */
const tooltipSlotProps = { tooltip: { sx: { fontSize: 12 } } } as const;

const SAVE_LABELS: Record<SummaryReportStatus, string> = {
    none: 'Save',
    clean: 'Up to Date',
    dirty: 'Update',
};

/** Wide enough for the longest label, so a change doesn't shuffle the header. */
const saveButtonSx = {
    width: 118,
    flexShrink: 0,
    whiteSpace: 'nowrap',
} as const;

const saveMenuButtonSx = {
    width: 32,
    minWidth: 32,
    flexShrink: 0,
    px: 0,
} as const;

const groupedButtonSx = {
    backgroundColor: 'background.paper',
    '&:hover': { backgroundColor: 'grey.100' },
} as const;

const disabledControlSx = {
    ...greyedSurface,
    '&.Mui-disabled': greyedSurface,
    '& .MuiToggleButton-root': greyedSurface,
    '& .MuiToggleButton-root.Mui-selected': greyedSurface,
    '& .MuiToggleButton-root.Mui-selected.Mui-disabled': greyedSurface,
} as const;

// ─── Component ───────────────────────────────────────────────────────────────

export default function SummaryReportsToolbar({
    activeReportName,
    reportStatus = 'none',
    chartVisible = false,
    onOpenDialog,
    layout,
    onLayoutChange,
}: SummaryReportToolbarProps) {
    // Both 'dirty' and 'clean' mean a report is loaded, so the label matches the action:
    // pressing it opens `update` whenever there is something to update.
    const saveLabel = SAVE_LABELS[reportStatus];

    // Nothing to save when the report already matches the view, so the button goes inert
    // rather than offering a write that changes nothing. A copy is still worth making, which
    // is the arrow's job.
    const saveDisabled = reportStatus === 'clean';

    // The alternate action needs something to copy.
    const copyDisabled = !activeReportName;

    const saveMenuAnchorRef = useRef<HTMLDivElement | null>(null);
    const [saveMenuOpen, setSaveMenuOpen] = useState(false);

    const handleSaveAsCopy = () => {
        setSaveMenuOpen(false);
        onOpenDialog('save');
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {/* Disabled until there is a chart to arrange. */}
            <Tooltip
                slotProps={tooltipSlotProps}
                describeChild
                title={
                    chartVisible
                        ? ''
                        : 'Add a chart first — the arrangement applies to the grid and chart together'
                }
            >
                <span>
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={layout}
                        disabled={!chartVisible}
                        onChange={(_, next) => {
                            if (next) {
                                onLayoutChange(next as SummaryReportLayout);
                            }
                        }}
                        aria-label='chart arrangement'
                        sx={{
                            ...raisedControlSx,
                            ...(chartVisible ? null : disabledControlSx),
                        }}
                    >
                        <ToggleButton value='stacked' aria-label='stacked'>
                            <Tooltip slotProps={tooltipSlotProps} title='Chart below the list'>
                                <HorizontalSplitIcon fontSize='small' />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value='sideBySide' aria-label='side by side'>
                            <Tooltip slotProps={tooltipSlotProps} title='Chart beside the list'>
                                <VerticalSplitIcon fontSize='small' />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </span>
            </Tooltip>

            <Divider orientation='vertical' flexItem sx={{ mx: 1, background: '#ccc' }} />

            {/* `describeChild` keeps the tooltip a description rather than the button's
                accessible name, so the aria-labels below can pin each name to its
                visible text. */}
            <Tooltip
                slotProps={tooltipSlotProps}
                describeChild
                title={
                    activeReportName
                        ? 'Share this report by link or with specific people'
                        : 'Save or load a report first — sharing sends the saved report, not the current view'
                }
            >
                <span>
                    <Button
                        size='small'
                        variant='outlined'
                        startIcon={<ShareIcon />}
                        onClick={() => onOpenDialog('share')}
                        disabled={!activeReportName}
                        sx={{
                            ...raisedControlSx,
                            ...(activeReportName ? null : disabledControlSx),
                        }}
                    >
                        Share
                    </Button>
                </span>
            </Tooltip>

            {/* A split button: the primary writes to the loaded report, or creates one; the
                arrow makes a copy. Grouped rather than set side by side, so the pair holds
                one width and the copy action doesn't come and go with the loaded report. */}
            <ButtonGroup
                variant='outlined'
                size='small'
                ref={saveMenuAnchorRef}
                aria-label='save report'
                sx={{ ...raisedControlSx, flexShrink: 0 }}
            >
                <Tooltip
                    slotProps={tooltipSlotProps}
                    describeChild
                    title={
                        saveDisabled
                            ? `"${activeReportName}" already matches the current view — use the arrow to save a copy`
                            : activeReportName
                              ? `Update "${activeReportName}" with the current columns, grouping, sorting and chart`
                              : 'Save the current columns, grouping, sorting and chart as a named report'
                    }
                >
                    {/* Wrapped because a disabled button fires no events, so the tooltip
                        needs a live element to hang off. */}
                    <span>
                        <Button
                            aria-label={saveLabel}
                            startIcon={<SaveIcon />}
                            disabled={saveDisabled}
                            onClick={() => onOpenDialog(activeReportName ? 'update' : 'save')}
                            sx={{
                                ...groupedButtonSx,
                                ...saveButtonSx,
                                ...(saveDisabled ? disabledControlSx : null),
                            }}
                        >
                            {saveLabel}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip
                    slotProps={tooltipSlotProps}
                    describeChild
                    title={copyDisabled ? 'No other options available' : 'More save options'}
                >
                    <span>
                        <Button
                            aria-label='more save options'
                            aria-haspopup='menu'
                            aria-controls={saveMenuOpen ? 'save-options-menu' : undefined}
                            aria-expanded={saveMenuOpen ? 'true' : undefined}
                            disabled={copyDisabled}
                            onClick={() => setSaveMenuOpen(true)}
                            sx={{
                                ...groupedButtonSx,
                                ...saveMenuButtonSx,
                                ...(copyDisabled ? disabledControlSx : null),
                            }}
                        >
                            <ArrowDropDownIcon fontSize='small' />
                        </Button>
                    </span>
                </Tooltip>
            </ButtonGroup>
            <Menu
                id='save-options-menu'
                anchorEl={saveMenuAnchorRef.current}
                open={saveMenuOpen}
                onClose={() => setSaveMenuOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handleSaveAsCopy}>
                    <ListItemIcon>
                        <SaveAsIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText primary='Save As Copy' secondary='Keeps the original untouched' />
                </MenuItem>
            </Menu>
            <Divider orientation='vertical' flexItem sx={{ mx: 1, background: '#ccc' }} />
            <Tooltip
                describeChild
                slotProps={tooltipSlotProps}
                title='Open a report you saved earlier, or one shared with you'
            >
                <Button
                    size='small'
                    variant='outlined'
                    aria-label='My Reports'
                    startIcon={<FolderOpenIcon />}
                    onClick={() => onOpenDialog('load')}
                    sx={raisedControlSx}
                >
                    My Reports
                </Button>
            </Tooltip>
        </Box>
    );
}
